import { DashboardCard } from "@/components/ui/DashboardCard/DashboardCard";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table/Table";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnFiltersState,
  type PaginationState,
  type SortingState,
} from "@tanstack/react-table";
import {
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { buildAssignRows } from "../../utils/assign/buildAssignRows";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select/Select";
import { HStack } from "@/components/layout/HStack";
import { Button } from "@/components/ui/Button/Button";
import {
  ArrowDownAZ,
  ArrowUpAZ,
  ArrowUpDown,
  Filter,
  Save,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu/DropdownMenu";
import {
  Pagination,
  PaginationButton,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/Pagination/Pagination";
import { Input } from "@/components/ui/Input/Input";
import { RESULTS_PER_PAGE_OPTIONS } from "@/constants/tableOptions";
import { useSearchParams } from "react-router-dom";
import { parseCsv, writeCsv } from "@/utils/csv";

export type PreferenceRow = {
  preferenceId: string;
  studentId: string;
  studentName: string;
  studentGrade: number;
  choice1: {
    id: string;
    resolved: boolean;
    name: string;
    min: number;
    max: number;
    enrolled: number;
  };
  choice2: {
    id: string;
    resolved: boolean;
    name: string;
    min: number;
    max: number;
    enrolled: number;
  };
  choice3: {
    id: string;
    resolved: boolean;
    name: string;
    min: number;
    max: number;
    enrolled: number;
  };
};

const getColumns = (
  assignments: { preference: string; exploration: string }[],
  setAssignments: Dispatch<
    SetStateAction<{ preference: string; exploration: string }[]>
  >,
  assignSingle: (assignment: {
    preference: string;
    exploration: string;
  }) => void,
): ColumnDef<PreferenceRow>[] => [
  {
    accessorKey: "studentName",
    header: ({ column }) => (
      <Button
        variant="ghost"
        style={{
          fontWeight: "var(--font-medium)",
          height: "fit-content",
          padding: "var(--space-xxs) var(--space-xs)",
          marginLeft: "calc(var(--space-xs) * -1)",
        }}
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Student{" "}
        {column.getIsSorted() === "asc" ? (
          <ArrowDownAZ />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowUpAZ />
        ) : (
          <ArrowUpDown />
        )}
      </Button>
    ),
  },
  {
    accessorKey: "studentGrade",
    header: ({ column }) => (
      <Button
        variant="ghost"
        style={{
          fontWeight: "var(--font-medium)",
          height: "fit-content",
          padding: "var(--space-xxs) var(--space-xs)",
          marginLeft: "calc(var(--space-xs) * -1)",
        }}
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Grade{" "}
        {column.getIsSorted() === "asc" ? (
          <ArrowDownAZ />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowUpAZ />
        ) : (
          <ArrowUpDown />
        )}
      </Button>
    ),
    filterFn: (row, columnId, filterValue) => {
      const selected = (filterValue as number[] | undefined) ?? [];
      if (selected.length === 0) return true;

      const grade = row.getValue(columnId) as number;
      return selected.includes(grade);
    },
  },
  {
    id: "choice1Name",
    accessorFn: (row) => row.choice1.name,
    header: ({ column }) => (
      <Button
        variant="ghost"
        style={{
          fontWeight: "var(--font-medium)",
          height: "fit-content",
          padding: "var(--space-xxs) var(--space-xs)",
          marginLeft: "calc(var(--space-xs) * -1)",
        }}
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Choice 1{" "}
        {column.getIsSorted() === "asc" ? (
          <ArrowDownAZ />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowUpAZ />
        ) : (
          <ArrowUpDown />
        )}
      </Button>
    ),
    cell: ({ row }) => (
      <span
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "0.125rem",
          width: "240px",
        }}
      >
        {row.original.choice1.name}
        <span
          style={{ display: "flex", gap: "0.25rem", alignItems: "flex-end" }}
        >
          ({row.original.choice1.enrolled}/{row.original.choice1.max})
          <span
            style={{
              fontSize: "var(--text-xs)",
              lineHeight: "var(--line-height-xs)",
              color: "var(--muted-foreground)",
            }}
          >
            Min: {row.original.choice1.min}
          </span>
        </span>
      </span>
    ),
  },
  {
    id: "choice2Name",
    accessorFn: (row) => row.choice2.name,
    header: ({ column }) => (
      <Button
        variant="ghost"
        style={{
          fontWeight: "var(--font-medium)",
          height: "fit-content",
          padding: "var(--space-xxs) var(--space-xs)",
          marginLeft: "calc(var(--space-xs) * -1)",
        }}
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Choice 2{" "}
        {column.getIsSorted() === "asc" ? (
          <ArrowDownAZ />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowUpAZ />
        ) : (
          <ArrowUpDown />
        )}
      </Button>
    ),
    cell: ({ row }) => (
      <span
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "0.125rem",
          width: "240px",
        }}
      >
        {row.original.choice2.name}
        <span
          style={{ display: "flex", gap: "0.25rem", alignItems: "flex-end" }}
        >
          ({row.original.choice2.enrolled}/{row.original.choice2.max})
          <span
            style={{
              fontSize: "var(--text-xs)",
              lineHeight: "var(--line-height-xs)",
              color: "var(--muted-foreground)",
            }}
          >
            Min: {row.original.choice2.min}
          </span>
        </span>
      </span>
    ),
  },
  {
    id: "choice3Name",
    accessorFn: (row) => row.choice3.name,
    header: ({ column }) => (
      <Button
        variant="ghost"
        style={{
          fontWeight: "var(--font-medium)",
          height: "fit-content",
          padding: "var(--space-xxs) var(--space-xs)",
          marginLeft: "calc(var(--space-xs) * -1)",
        }}
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Choice 3{" "}
        {column.getIsSorted() === "asc" ? (
          <ArrowDownAZ />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowUpAZ />
        ) : (
          <ArrowUpDown />
        )}
      </Button>
    ),
    cell: ({ row }) => (
      <span
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "0.125rem",
          width: "240px",
        }}
      >
        {row.original.choice3.name}
        <span
          style={{ display: "flex", gap: "0.25rem", alignItems: "flex-end" }}
        >
          ({row.original.choice3.enrolled}/{row.original.choice3.max})
          <span
            style={{
              fontSize: "var(--text-xs)",
              lineHeight: "var(--line-height-xs)",
              color: "var(--muted-foreground)",
            }}
          >
            Min: {row.original.choice3.min}
          </span>
        </span>
      </span>
    ),
  },
  {
    id: "assign",
    header: "Assign",
    cell: ({ row }) => {
      const r = row.original;
      const value = assignments.find(
        (a) => a.preference === r.preferenceId,
      )?.exploration;

      // A preference can carry fewer than three choices, or point at an
      // exploration that is not in this session (archived, deleted, moved).
      // Radix throws on an empty item value, so only offer real choices.
      const choices = [r.choice1, r.choice2, r.choice3].filter(
        (c, i, all) =>
          c.id && c.resolved && all.findIndex((o) => o.id === c.id) === i,
      );

      return (
        <Select
          value={value}
          onValueChange={(v) =>
            setAssignments((prev) => {
              const idx = prev.findIndex(
                (a) => a.preference === r.preferenceId,
              );

              if (idx === -1) {
                return [
                  ...prev,
                  { preference: r.preferenceId, exploration: v },
                ];
              }

              return prev.map((a, i) =>
                i === idx ? { ...a, exploration: v } : a,
              );
            })
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Select choice" />
          </SelectTrigger>
          <SelectContent>
            {choices.length ? (
              choices.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))
            ) : (
              <SelectItem disabled value=" ">
                No available choices
              </SelectItem>
            )}
          </SelectContent>
        </Select>
      );
    },
  },
  {
    id: "actions",
    header: () => (
      <div style={{ textAlign: "right", fontWeight: "var(--font-medium)" }}>
        Actions
      </div>
    ),
    cell: ({ row }) => {
      const preference = row.original;

      return (
        <HStack justify="flex-end">
          <Button
            size="icon"
            disabled={
              !assignments.find((a) => a.preference === preference.preferenceId)
            }
            onClick={() => {
              const exploration = assignments.find(
                (a) => a.preference === preference.preferenceId,
              );
              if (!exploration) return;
              assignSingle({
                preference: preference.preferenceId,
                exploration: exploration.exploration,
              });
            }}
          >
            <Save />
          </Button>
        </HStack>
      );
    },
  },
];

const DataTable = ({
  data,
  columns,
}: {
  data: PreferenceRow[];
  columns: ColumnDef<PreferenceRow>[];
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  // Sorting
  const sortKRaw = searchParams.get("sortK");
  const sortVRaw = searchParams.get("sortV");

  const urlSorting = useMemo<SortingState>(() => {
    const validK =
      sortKRaw === "studentName" ||
      sortKRaw === "studentGrade" ||
      sortKRaw === "choice1Name" ||
      sortKRaw === "choice2Name" ||
      sortKRaw === "choice3Name";

    const validV = sortVRaw === "asc" || sortVRaw === "desc";

    if (!validK || !validV) return [];
    return [{ id: sortKRaw, desc: sortVRaw === "desc" }];
  }, [sortKRaw, sortVRaw]);

  const onSortingChange = (
    updater: SortingState | ((old: SortingState) => SortingState),
  ) => {
    const current = urlSorting;
    const next = typeof updater === "function" ? updater(current) : updater;

    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);

      const first = next[0];
      if (!first) {
        sp.delete("sortK");
        sp.delete("sortV");
        return sp;
      }

      sp.set("sortK", first.id);
      sp.set("sortV", first.desc ? "desc" : "asc");
      return sp;
    });
  };

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onPaginationChange: setPagination,
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      sorting: urlSorting,
      columnFilters,
      pagination,
    },
    autoResetPageIndex: false,
  });

  // Grade Filtering
  const urlSelectedGrades = useMemo(
    () =>
      parseCsv(searchParams.get("grade"))
        .map((g) => Number(g))
        .filter((n) => Number.isFinite(n)),
    [searchParams],
  );

  const grades = useMemo(() => {
    const set = new Set<number>();
    for (const d of data) if (d.studentGrade) set.add(d.studentGrade);
    return Array.from(set).sort((a, b) => a - b);
  }, [data]);

  const toggleGrade = (grade: number) => {
    const next = urlSelectedGrades.includes(grade)
      ? urlSelectedGrades.filter((s) => s !== grade)
      : [...urlSelectedGrades, grade];

    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      writeCsv(sp, "grade", next);
      return sp;
    });
  };

  const clearGradeFilter = () => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      sp.delete("grade");
      return sp;
    });
  };

  const urlSearch = useMemo(() => searchParams.get("q") ?? "", [searchParams]);

  const totalCount = table.getCoreRowModel().rows.length;
  const filteredCount = table.getFilteredRowModel().rows.length;

  useEffect(() => {
    table.getColumn("studentGrade")?.setFilterValue(urlSelectedGrades);
    table.getColumn("studentName")?.setFilterValue(urlSearch);
    table.setPageIndex(0);
  }, [table, urlSelectedGrades, urlSearch]);

  // Pagination
  const showPagination = table.getPageCount() > 1;

  function getPageItems(current: number, total: number) {
    const c = current + 1;
    const t = total;

    if (t <= 7) return Array.from({ length: t }, (_, i) => i + 1);

    const items: (number | "…")[] = [1];

    if (c > 3) items.push("…");

    const start = Math.max(2, c - 1);
    const end = Math.min(t - 1, c + 1);

    for (let p = start; p <= end; p++) items.push(p);

    if (c < t - 2) items.push("…");

    items.push(t);
    return items;
  }

  return (
    <>
      <HStack align="center" justify="space-between" wrap>
        <HStack align="center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                style={{
                  height: "fit-content",
                  padding: "var(--space-xxs) var(--space-xs)",
                }}
              >
                <Filter /> Filter
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>Grades</DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  {grades.map((g) => (
                    <DropdownMenuCheckboxItem
                      key={g}
                      checked={urlSelectedGrades.includes(g)}
                      onCheckedChange={() => toggleGrade(g)}
                      onSelect={(e) => e.preventDefault()}
                    >
                      {g}
                    </DropdownMenuCheckboxItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault();
                      clearGradeFilter();
                    }}
                    disabled={urlSelectedGrades.length === 0}
                  >
                    Clear
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            </DropdownMenuContent>
          </DropdownMenu>
          <p
            style={{
              color: "var(--muted-foreground)",
            }}
          >
            {filteredCount} of {totalCount} students
          </p>
        </HStack>
        <HStack align="center">
          <div style={{ width: "240px", maxWidth: "100%" }}>
            <Input
              placeholder="Search students..."
              value={urlSearch}
              onChange={(e) => {
                const val = e.target.value;

                setSearchParams((prev) => {
                  const sp = new URLSearchParams(prev);

                  if (!val) sp.delete("q");
                  else sp.set("q", val);

                  return sp;
                });
              }}
            />
          </div>
          <Select
            value={String(table.getState().pagination.pageSize)}
            onValueChange={(v) => {
              table.setPageSize(Number(v));
              table.setPageIndex(0);
            }}
          >
            <SelectTrigger
              style={{
                width: "fit-content",
                height: "fit-content",
                padding: "var(--space-xxs) var(--space-xs)",
              }}
            >
              <SelectValue placeholder="Select results per page" />
            </SelectTrigger>
            <SelectContent>
              {RESULTS_PER_PAGE_OPTIONS.map((option) => (
                <SelectItem key={option} value={String(option)}>
                  {option} results/page
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </HStack>
      </HStack>
      <Table borderColor="var(--secondary-border)">
        <TableHeader>
          {table.getHeaderGroups().map((h) => (
            <TableRow key={h.id}>
              {h.headers.map((header) => (
                <TableHead key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    style={
                      ["choice1Name", "choice2Name", "choice3Name"].includes(
                        cell.column.id,
                      )
                        ? {
                            whiteSpace: "normal",
                            overflowWrap: "anywhere",
                            wordBreak: "break-word",
                          }
                        : undefined
                    }
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length}>No records</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {showPagination && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={(e) => {
                  e.preventDefault();
                  table.previousPage();
                }}
                disabled={!table.getCanPreviousPage()}
              />
            </PaginationItem>

            {getPageItems(pagination.pageIndex, table.getPageCount()).map(
              (it, idx) =>
                it === "…" ? (
                  <PaginationItem key={`el-${idx}`}>
                    <PaginationEllipsis />
                  </PaginationItem>
                ) : (
                  <PaginationItem key={it}>
                    <PaginationButton
                      isActive={it === pagination.pageIndex + 1}
                      onClick={(e) => {
                        e.preventDefault();
                        table.setPageIndex(it - 1);
                      }}
                    >
                      {it}
                    </PaginationButton>
                  </PaginationItem>
                ),
            )}

            <PaginationItem>
              <PaginationNext
                onClick={(e) => {
                  e.preventDefault();
                  table.nextPage();
                }}
                disabled={!table.getCanNextPage()}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </>
  );
};

const AssignPreferencesTable = ({
  data,
  assignments,
  setAssignments,
  assign,
}: {
  data: { preferences: any[]; explorations: any[]; students: any[] };
  assignments: { preference: string; exploration: string }[];
  setAssignments: Dispatch<
    SetStateAction<{ preference: string; exploration: string }[]>
  >;
  assign: {
    assignSingle: (assignment: {
      preference: string;
      exploration: string;
    }) => void;
    loading: boolean;
  };
}) => {
  const rows = useMemo(() => buildAssignRows(data), [data]);
  const columns = useMemo(
    () => getColumns(assignments, setAssignments, assign.assignSingle),
    [assignments, setAssignments, assign.assignSingle],
  );

  return (
    <DashboardCard>
      <DataTable data={rows} columns={columns} />
    </DashboardCard>
  );
};

export { AssignPreferencesTable };
